<template>
  <div>
    <PageHeader title="Insurer reportings">
      <div class="ml-auto">
        <GenericButton @click="bulkTransferFormModalOpen = true">Exporter</GenericButton>
        <Modal :open="bulkTransferFormModalOpen" @close="bulkTransferFormModalOpen = false">
          <div class="p-4">
            <h2 class="text-lg font-bold">Exporter le fichier de virements aux assureurs</h2>
            <p class="text-sm text-gray-500">Sélectionnez les données à exporter</p>

            <BulkTransferForm @success="bulkTransferFormModalOpen = false" />
          </div>
        </Modal>
      </div>
    </PageHeader>
    <div class="mb-6 grid grid-cols-1">
      <div class="px-4 py-5 bg-white shadow-sm rounded-lg sm:px-5 sm:py-3 font-bold">
        <p class="text-gray-900 text-md">Récapitulatif</p>
        <hr class="my-2 border-gray-300" />
        <div class="grid grid-rows-4 md:grid-cols-4 md:grid-rows-none mt-2">
          <div>
            <p class="text-gray-700 font-normal">Montant à verser aux assureurs</p>
            <p class="text-red-700 font-light text-2xl">
              {{ formatPrice(totalCommissionsAmountDueToInsurer(allInsurerReportings)) }}
            </p>
          </div>
          <div>
            <p class="text-gray-700 font-normal">Montant à percevoir des assureurs</p>
            <p class="text-green-700 font-light text-2xl">
              {{ formatPrice(totalClaimsAmountDueToEstaly(allInsurerReportings)) }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="mb-6 flex w-full gap-10 items-center">
      <MonthlyDateFilterSelector
        :key="periodSelectorKey"
        class="w-1/4"
        placeholder="Sélectionner une période"
        @newDateSelected="updateDateFilters"
      />
      <SearchBarFilter
        v-for="(filterCategory, filtersName, filterIndex) in availableFiltersWithOptions"
        :key="filterIndex"
        :filterName="filtersName"
        :filterIdx="filterIndex"
        :current-page="currentPage"
        :available-filters="filters"
        :selected-filters="currentFilteringData"
        @updateFilterSearch="updateCurrentFilteringData"
      />
      <ClearFiltersButton v-if="hasActiveFilters" @click="clearFilters" />
    </div>
    <Table :columns="columns" :data="index" :is-downloadable="true" @download="downloadReporting">
      <template #commissionsPaymentStatus="{ row, column }">
        <select
          :value="row[column.field]"
          @change="(event) => handleStatusUpdate({ field: column.field, row, value: event.target.value })"
          :class="[
            'block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary sm:text-sm sm:leading-6',
            getSelectClass(row[column.field], column),
          ]"
        >
          <option v-for="option in column.options" :key="option" :value="option">
            {{ $t(option) }}
          </option>
        </select>
      </template>
      <template #claimsPaymentStatus="{ row, column }">
        <select
          :value="row[column.field]"
          @change="(event) => handleStatusUpdate({ field: column.field, row, value: event.target.value })"
          :class="[
            'block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary sm:text-sm sm:leading-6',
            getSelectClass(row[column.field], column),
          ]"
        >
          <option v-for="option in column.options" :key="option" :value="option">
            {{ $t(option) }}
          </option>
        </select>
      </template>
    </Table>
    <Pagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      :totalCount="totalCount"
      @changePage="handleChangePage"
    />
  </div>
</template>

<script>
import {
  ClearFiltersButton,
  Modal,
  Pagination,
  PageHeader,
  Table,
  SearchBarFilter,
  GenericButton,
  formatPrice,
} from "@estaly/ui";
import MonthlyDateFilterSelector from "@/components/menu/MonthlyDateFilterSelector.vue";
import BulkTransferForm from "@/components/report/BulkTransferForm.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { toCamelCase } from "@/utils/utils";

export default {
  components: {
    Pagination,
    GenericButton,
    Modal,
    PageHeader,
    BulkTransferForm,
    MonthlyDateFilterSelector,
    SearchBarFilter,
    ClearFiltersButton,
    Table,
  },

  computed: {
    ...mapState("insurerReportings", ["index", "totalPages", "totalCount"]),
    ...mapGetters("insurerReportings", [
      "totalCommissionsAmountDueToInsurer",
      "totalClaimsAmountDueToEstaly",
      "getCommissionPaymentStatuses",
      "getClaimsPaymentStatuses",
    ]),
    ...mapState("insurers", { insurers: "index" }),
    ...mapState("contractDistributions", { contractDistributions: "index" }),

    availableFiltersWithOptions() {
      return Object.fromEntries(
        Object.entries(this.filters).filter(([filterKey, filterOptions]) => filterKey && filterOptions?.length > 1),
      );
    },

    hasActiveFilters() {
      return (
        Object.keys(this.availableFiltersWithOptions).some(
          (filterName) => this.currentFilteringData[filterName]?.length > 0,
        ) || Boolean(this.currentFilteringData.startDate)
      );
    },

    filters() {
      return {
        insurers: this.insurers?.map((insurer) => insurer.name) || [],
        insurerContractGroupReferences:
          [
            ...new Set(
              this.contractDistributions?.map(
                (contractDistribution) => contractDistribution.insurerContractGroupReference,
              ),
            ),
          ] || [],
        commissionsPaymentStatus: this.getCommissionPaymentStatuses() || [],
        claimsPaymentStatus: this.getClaimsPaymentStatuses() || [],
      };
    },
  },

  data() {
    return {
      bulkTransferFormModalOpen: false,
      columns: [
        { label: "Assureur", field: "insurerName" },
        { label: "Numéro de police", field: "insurerContractGroupReference" },
        { label: "Période", field: "period" },
        { label: "Montant primes à verser", field: "commissionsAmountDueToInsurer", numeric: true, price: true },
        { label: "Montant sinistres à percevoir", field: "claimsAmountDueToEstaly", numeric: true, price: true },
        {
          label: "Statut paiement primes",
          field: "commissionsPaymentStatus",
          slot: "commissionsPaymentStatus",
          type: "selector",
          options: ["pending", "paid"],
          colors: {
            pending: "bg-yellow-50 text-yellow-600",
            paid: "bg-green-50 text-green-600",
          },
        },
        {
          label: "Statut paiement sinistres",
          field: "claimsPaymentStatus",
          slot: "claimsPaymentStatus",
          type: "selector",
          options: ["pending", "paid"],
          colors: {
            pending: "bg-yellow-50 text-yellow-600",
            paid: "bg-green-50 text-green-600",
          },
        },
      ],
      currentPage: 1,
      periodSelectorKey: 0,
      allInsurerReportings: [],
      currentFilteringData: {
        insurers: [],
        insurerContractGroupReferences: [],
        commissionsPaymentStatus: [],
        claimsPaymentStatus: [],
        startDate: null,
        endDate: null,
      },
    };
  },

  async mounted() {
    await this.withLoader(async () => {
      await this.retrieveInsurerReportings();
      await this.fetchInsurersIndex({ perPage: 2000 });
      await this.fetchContractDistributionsIndex({ page: 1, perPage: 2000 });
      await this.retrieveAllInsurerReportings();
    });
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("backoffice", ["withLoader"]),
    ...mapActions("insurerReportings", ["fetchIndex", "updateInsurerReporting"]),
    ...mapActions("insurers", { fetchInsurersIndex: "fetchIndex" }),
    ...mapActions("contractDistributions", { fetchContractDistributionsIndex: "fetchIndex" }),
    formatPrice,

    updateDateFilters: function (startDate, endDate) {
      this.currentFilteringData["startDate"] = startDate;
      this.currentFilteringData["endDate"] = endDate;
      this.handleChangePage(1);
    },

    updateCurrentFilteringData: function (filterName, filterOptions) {
      this.currentFilteringData[filterName] = filterOptions;
      this.handleChangePage(1);
    },

    clearFilters() {
      this.currentFilteringData = {
        insurers: [],
        insurerContractGroupReferences: [],
        commissionsPaymentStatus: [],
        claimsPaymentStatus: [],
        startDate: null,
        endDate: null,
      };
      this.periodSelectorKey += 1;
      this.handleChangePage(1);
    },

    handleChangePage(page) {
      this.currentPage = page;
      this.retrieveInsurerReportings();
    },

    getSelectClass(value, column) {
      if (column.type === "selector" && column.colors && column.colors[value]) return column.colors[value];
      return "bg-white text-gray-900";
    },

    downloadReporting(insurerReporting) {
      window.open(insurerReporting.fileUrl, "_blank");
    },

    async retrieveInsurerReportings() {
      await this.withLoader(async () => {
        await this.fetchIndex({ page: this.currentPage, ...this.currentFilteringData });
      });
    },

    async retrieveAllInsurerReportings() {
      await this.withLoader(async () => {
        const response = await this.fetchIndex({ save: false, perPage: 2000 });
        if (response?.success) this.allInsurerReportings = toCamelCase(response.data.records);
      });
    },

    async handleStatusUpdate({ field, row, value }) {
      const data = {
        [field]: value,
      };
      const { success } = await this.updateInsurerReporting({ id: row.id, data: data });
      if (!success) return;

      this.notify({
        category: "simple",
        type: "success",
        title: "Status updated",
      });
      await this.retrieveInsurerReportings();
    },
  },
};
</script>
